import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { TrendingUp, TrendingDown, DollarSign, Receipt, Minus, Equal, Target, UtensilsCrossed, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (month) => {
    const [year, m] = month.split('-');
    const months = {
        '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
        '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
        '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
    };
    return `${months[m] || m} ${year}`;
};

const ProfitLossPage = () => {
    const { api, hasPermission, user } = useAuth();
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
    const [daysOpen, setDaysOpen] = useState(26);
    const [data, setData] = useState(null);
    const [overview, setOverview] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('detail');

    const canEdit = hasPermission ? hasPermission('edit_expense') : false;

    useEffect(() => {
        if (viewMode === 'detail') {
            fetchMonthData();
        } else {
            fetchOverview();
        }
    }, [selectedMonth, viewMode, daysOpen]);

    const fetchMonthData = async () => {
        setLoading(true);
        try {
            const res = await api().get(`/profit-loss/${selectedMonth}?days_open=${daysOpen}`);
            setData(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const fetchOverview = async () => {
        setLoading(true);
        try {
            const res = await api().get('/profit-loss?months=6');
            setOverview(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const getMonthOptions = () => {
        const options = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            options.push({ value, label: formatMonthLabel(value) });
        }
        return options;
    };

    const getBreakEvenStatusBadge = (status) => {
        switch (status) {
            case 'ABOVE':
                return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Dépassé</Badge>;
            case 'REACHED':
                return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Atteint</Badge>;
            case 'CLOSE':
                return <Badge className="bg-orange-100 text-orange-700"><Target className="w-3 h-3 mr-1" />Proche</Badge>;
            case 'BELOW':
                return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />En dessous</Badge>;
            case 'IMPOSSIBLE':
                return <Badge className="bg-gray-100 text-gray-700">Marge insuffisante</Badge>;
            default:
                return <Badge variant="outline">Données insuffisantes</Badge>;
        }
    };

    const ResultLine = ({ label, value, formatted, type = 'normal', icon: Icon, indent = false }) => (
        <div className={`flex justify-between items-center py-3 px-4 ${
            type === 'total' ? 'bg-slate-100 font-bold text-lg' :
            type === 'subtotal' ? 'bg-slate-50 font-medium' :
            type === 'result' ? 'bg-primary/10 font-bold text-lg' :
            ''
        } ${indent ? 'pl-8' : ''}`}>
            <div className="flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4" />}
                <span>{label}</span>
            </div>
            <span className={`font-mono ${
                type === 'result' && value < 0 ? 'text-red-600' :
                type === 'result' && value > 0 ? 'text-green-600' :
                ''
            }`}>
                {formatted}
            </span>
        </div>
    );

    return (
        <div className="space-y-6" data-testid="profit-loss-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Compte de Résultat</h1>
                    <p className="text-muted-foreground">Analyse de la rentabilité et seuil de rentabilité</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Select value={viewMode} onValueChange={setViewMode}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="detail">Vue détaillée</SelectItem>
                            <SelectItem value="overview">Vue d'ensemble</SelectItem>
                        </SelectContent>
                    </Select>
                    {viewMode === 'detail' && (
                        <>
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger className="w-48" data-testid="month-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {getMonthOptions().map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2">
                                <Label className="text-sm whitespace-nowrap">Jours ouvrés:</Label>
                                <Input 
                                    type="number" 
                                    min="1" 
                                    max="31"
                                    value={daysOpen}
                                    onChange={(e) => setDaysOpen(parseInt(e.target.value) || 26)}
                                    className="w-16"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {viewMode === 'detail' && data && (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-muted-foreground">Chiffre d'affaires</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{data.total_revenue_formatted}</p>
                                <p className="text-xs text-muted-foreground">{data.sales_count} journée(s) validée(s)</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-muted-foreground">Marge brute</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-blue-600">{data.gross_margin_formatted}</p>
                                <p className="text-xs text-muted-foreground">{data.gross_margin_percent}% du CA</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-muted-foreground">Charges totales</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-orange-600">{data.total_expenses_formatted}</p>
                                <p className="text-xs text-muted-foreground">
                                    Fixes: {data.total_fixed_expenses?.toLocaleString() || 0} CFA
                                </p>
                            </CardContent>
                        </Card>
                        <Card className={data.is_profitable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-muted-foreground">Résultat net</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className={`text-2xl font-bold ${data.is_profitable ? 'text-green-600' : 'text-red-600'}`}>
                                    {data.net_result_formatted}
                                </p>
                                <p className="text-xs text-muted-foreground">{data.net_margin_percent}% de marge nette</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Break-even Analysis */}
                    <Card className="border-primary/20">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="w-5 h-5 text-primary" />
                                Seuil de Rentabilité
                            </CardTitle>
                            <CardDescription>
                                Nombre d'assiettes nécessaires pour couvrir les charges
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-slate-50 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Assiettes vendues</p>
                                    <p className="text-2xl font-bold">{data.plates_sold}</p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Marge moyenne/assiette</p>
                                    <p className="text-2xl font-bold">
                                        {data.avg_margin_per_plate_formatted || '—'}
                                    </p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Seuil mensuel</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {data.break_even_plates_month !== null ? `${data.break_even_plates_month} assiettes` : '—'}
                                    </p>
                                </div>
                                <div className="p-4 bg-primary/10 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Objectif/jour ({daysOpen}j)</p>
                                    <p className="text-2xl font-bold text-primary">
                                        {data.break_even_plates_day !== null ? `${data.break_even_plates_day} assiettes` : '—'}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Statut:</span>
                                    {getBreakEvenStatusBadge(data.break_even_status)}
                                </div>
                                {data.break_even_plates_month && data.plates_sold > 0 && (
                                    <div className="text-sm text-muted-foreground">
                                        Progression: {Math.round((data.plates_sold / data.break_even_plates_month) * 100)}%
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Detailed P&L Statement */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Détail du compte de résultat - {data.month_label}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="divide-y">
                                <ResultLine 
                                    label="CHIFFRE D'AFFAIRES" 
                                    value={data.total_revenue}
                                    formatted={data.total_revenue_formatted}
                                    type="total"
                                    icon={DollarSign}
                                />
                                <ResultLine 
                                    label={`Ventes validées (${data.sales_count} jour${data.sales_count > 1 ? 's' : ''})`}
                                    value={data.total_revenue}
                                    formatted={data.total_revenue_formatted}
                                    indent
                                />
                                <ResultLine 
                                    label="COÛT MATIÈRE" 
                                    value={-data.total_material_cost}
                                    formatted={`- ${data.total_material_cost_formatted}`}
                                    type="subtotal"
                                    icon={Minus}
                                />
                                <ResultLine 
                                    label={`MARGE BRUTE (${data.gross_margin_percent}%)`}
                                    value={data.gross_margin}
                                    formatted={data.gross_margin_formatted}
                                    type="total"
                                    icon={Equal}
                                />
                                <ResultLine 
                                    label="CHARGES D'EXPLOITATION" 
                                    value={-data.total_expenses}
                                    formatted={`- ${data.total_expenses_formatted}`}
                                    type="subtotal"
                                    icon={Minus}
                                />
                                <ResultLine 
                                    label="Charges fixes"
                                    value={-data.total_fixed_expenses}
                                    formatted={`- ${data.total_fixed_expenses?.toLocaleString() || 0} CFA`}
                                    indent
                                />
                                <ResultLine 
                                    label="Charges variables"
                                    value={-data.total_variable_expenses}
                                    formatted={`- ${data.total_variable_expenses?.toLocaleString() || 0} CFA`}
                                    indent
                                />
                                <ResultLine 
                                    label={`RÉSULTAT NET (${data.net_margin_percent}%)`}
                                    value={data.net_result}
                                    formatted={data.net_result_formatted}
                                    type="result"
                                    icon={data.is_profitable ? TrendingUp : TrendingDown}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {viewMode === 'overview' && (
                <Card>
                    <CardHeader>
                        <CardTitle>Évolution sur 6 mois</CardTitle>
                        <CardDescription>Comparaison des résultats mensuels</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Mois</TableHead>
                                    <TableHead className="text-right">CA</TableHead>
                                    <TableHead className="text-right">Marge brute</TableHead>
                                    <TableHead className="text-right">Charges</TableHead>
                                    <TableHead className="text-right">Résultat</TableHead>
                                    <TableHead className="text-right">Assiettes</TableHead>
                                    <TableHead className="text-center">Rentable</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8">
                                            Chargement...
                                        </TableCell>
                                    </TableRow>
                                ) : overview.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            Aucune donnée
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    overview.map((item) => (
                                        <TableRow key={item.month}>
                                            <TableCell className="font-medium">{item.month_label}</TableCell>
                                            <TableCell className="text-right font-mono">
                                                {item.total_revenue_formatted}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-blue-600">
                                                {item.gross_margin_formatted}
                                                <span className="text-xs text-muted-foreground ml-1">
                                                    ({item.gross_margin_percent}%)
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-orange-600">
                                                {item.total_expenses_formatted}
                                            </TableCell>
                                            <TableCell className={`text-right font-mono font-bold ${
                                                item.is_profitable ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {item.net_result_formatted}
                                            </TableCell>
                                            <TableCell className="text-right font-mono">
                                                {item.plates_sold || 0}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {item.is_profitable ? (
                                                    <Badge className="bg-green-100 text-green-700">
                                                        <TrendingUp className="w-3 h-3 mr-1" />
                                                        Oui
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-red-100 text-red-700">
                                                        <TrendingDown className="w-3 h-3 mr-1" />
                                                        Non
                                                    </Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default ProfitLossPage;
